import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { 
  Home,
  Store, 
  Calendar, 
  ShoppingBag, 
  Users, 
  CalendarDays, 
  History, 
  Settings, 
  Briefcase, 
  Heart, 
  CalendarCheck,
  Wrench,
  BarChart2,
  Shield,
  HelpCircle,
  LogOut,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  Tag,
  Package,
  FileText,
  Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useToast } from "@/hooks/use-toast";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetTrigger 
} from "@/components/ui/sheet";

interface MobileSidebarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
}

interface MenuItem {
  icon: React.ComponentType<any>;
  label: string;
  href: string;
  active?: boolean;
  badge?: string;
  disabled?: boolean;
}

interface MenuGroup {
  label: string;
  icon: React.ComponentType<any>;
  items: MenuItem[];
}

const mainMenuItems: MenuItem[] = [
  { icon: Home, label: "Início", href: "/app/home" },
  { icon: ShoppingBag, label: "Meus Produtos", href: "/app/meus-produtos" },
  { icon: Store, label: "Loja de Serviços", href: "/app/loja-de-servicos" },
  { icon: ShoppingCart, label: "Carrinho", href: "/app/carrinho" },
  { icon: Package, label: "Meus Pedidos", href: "/app/meus-pedidos" },
  { icon: Tag, label: "Cupons de Desconto", href: "/app/cupons", disabled: true },
  { icon: Calendar, label: "Consultas & Agendamentos", href: "/app/consultas", disabled: true },
  { icon: Users, label: "Comunidade", href: "/app/comunidade" },
];

const professionalMenu: MenuGroup = {
  label: "Menu Profissional",
  icon: Briefcase,
  items: [
    { icon: Heart, label: "Meus Clientes", href: "/app/prof/clientes" },
    { icon: CalendarCheck, label: "Minha Agenda Profissional", href: "/app/prof/agenda" },
    { icon: Wrench, label: "Meus Serviços", href: "/app/prof/servicos" },
    { icon: BarChart2, label: "Relatórios Profissionais", href: "/app/prof/relatorios" },
  ]
};

const adminMenu: MenuGroup = {
  label: "Menu Admin",
  icon: Shield,
  items: [
    { icon: Settings, label: "Gestão de Usuários", href: "/app/admin/usuarios" },
    { icon: Wrench, label: "Gestão de Serviços", href: "/app/admin/servicos" },
    { icon: FileText, label: "Gestão do Blog", href: "/app/admin/blog" },
    { icon: FileText, label: "CMS Landing Pages", href: "/app/admin/cms-landing-pages" },
    { icon: BarChart2, label: "Gestão de Campanhas", href: "/app/admin/campanhas" },
    { icon: Gift, label: "Concessões Manuais", href: "/app/admin/concessoes-manuais" },
    { icon: BarChart2, label: "Relatórios Gerais", href: "/app/admin/relatorios" },
    { icon: Shield, label: "Moderação de Conteúdo", href: "/app/admin/moderacao" },
  ]
};

export function MobileSidebar({ isOpen, onOpenChange, trigger }: MobileSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuthContext();
  const { toast } = useToast();
  const { role } = useUserProfile();
  const [professionalExpanded, setProfessionalExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);

  // Check if current path matches the item href
  const isActiveItem = (href: string) => location.pathname === href;
  
  // Check if any item in a group is active
  const hasActiveGroupItem = (group: MenuGroup) => 
    group.items.some(item => isActiveItem(item.href));

  // Auto-expand groups that contain the active item
  useEffect(() => {
    if (hasActiveGroupItem(professionalMenu)) {
      setProfessionalExpanded(true);
    }
    if (hasActiveGroupItem(adminMenu)) {
      setAdminExpanded(true);
    }
  }, [location.pathname]);

  // Close sidebar when navigation occurs
  useEffect(() => {
    onOpenChange(false);
  }, [location.pathname, onOpenChange]);

  const handleLogout = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        toast({
          title: "Erro",
          description: "Falha ao fazer logout",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Sucesso",
          description: "Logout realizado com sucesso!",
        });
        navigate('/app/login');
      }
    } catch (error) {
      console.error('Erro no logout:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado ao fazer logout",
        variant: "destructive",
      });
    }
  };

  const MenuItemComponent = ({ item, isSubItem = false }: { item: MenuItem; isSubItem?: boolean }) => {
    const isActive = isActiveItem(item.href);
    
    if (item.disabled) {
      return (
        <div className={`
          flex items-center p-3 rounded transition-all duration-200
          text-muted-foreground cursor-not-allowed opacity-60
          ${isSubItem ? 'text-sm ml-6' : ''}
        `}>
          <item.icon className="w-5 h-5 mr-3" />
          <span>{item.label}</span>
          <span className="ml-auto text-xs text-muted-foreground italic">em breve</span>
        </div>
      );
    }
    
    return (
      <Link
        to={item.href}
        className={`
          flex items-center p-3 rounded transition-all duration-200
          ${isActive 
            ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-l-4 border-l-yellow-500 font-medium' 
            : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
          }
          ${isSubItem ? 'text-sm ml-6' : ''}
        `}
      >
        <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-yellow-600 dark:text-yellow-400' : ''}`} />
        <span>{item.label}</span>
        {item.badge && (
          <span className="ml-auto bg-sidebar-primary text-sidebar-primary-foreground text-xs px-2 py-1 rounded-full">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  const MenuGroupComponent = ({ group, expanded, onToggle }: { 
    group: MenuGroup; 
    expanded: boolean; 
    onToggle: () => void; 
  }) => (
    <div className="mt-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 rounded text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200"
      >
        <div className="flex items-center">
          <group.icon className="w-5 h-5 mr-3" />
          <span className="font-medium">{group.label}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className="ml-6 mt-2 space-y-1">
          {group.items.map((item, index) => (
            <MenuItemComponent key={index} item={item} isSubItem />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        {trigger}
      </SheetTrigger>
      <SheetContent side="left" className="w-80 bg-sidebar text-sidebar-foreground p-0 max-h-screen">
        <div className="flex flex-col h-full max-h-screen">
          {/* Header */}
          <SheetHeader className="p-4 border-b border-sidebar-border flex-shrink-0">
            <SheetTitle className="text-left">
              <img src="/src/assets/logo.jpg" alt="Logo" className="h-8 w-auto" />
            </SheetTitle>
          </SheetHeader>

          {/* Navigation - Scrollable */}
          <div className="flex-1 overflow-y-auto px-2 py-4">
            {/* Main Menu */}
            <ul className="space-y-1">
              {mainMenuItems.map((item, index) => (
                <li key={index}>
                  <MenuItemComponent item={item} />
                </li>
              ))}
            </ul>

            {/* Professional Menu */}
            {(role === 'profissional' || role === 'super_administrador') && (
              <div className="mt-6 pt-4 border-t border-sidebar-border">
                <MenuGroupComponent 
                  group={professionalMenu}
                  expanded={professionalExpanded}
                  onToggle={() => setProfessionalExpanded(!professionalExpanded)}
                />
              </div>
            )}

            {/* Admin Menu */}
            {role === 'super_administrador' && (
              <MenuGroupComponent 
                group={adminMenu}
                expanded={adminExpanded}
                onToggle={() => setAdminExpanded(!adminExpanded)}
              />
            )}
          </div>

          {/* Footer Utilities - Fixed at bottom */}
          <div className="flex-shrink-0 p-4 border-t border-sidebar-border">
            <ul className="space-y-2">
              <li>
                <Link
                  to="/app/perfil"
                  className={`
                    flex items-center p-3 rounded transition-all duration-200
                    ${isActiveItem("/app/perfil") 
                      ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-l-4 border-l-yellow-500 font-medium' 
                      : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
                    }
                  `}
                >
                  <Settings className={`w-5 h-5 mr-3 ${isActiveItem("/app/perfil") ? 'text-yellow-600 dark:text-yellow-400' : ''}`} />
                  <span>Perfil e Configurações</span>
                </Link>
              </li>
              <li>
                <ThemeToggle showLabel={true} />
              </li>
              <li>
                <Link
                  to="/app/suporte"
                  className="flex items-center p-3 rounded text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200"
                >
                  <HelpCircle className="w-5 h-5 mr-3" />
                  <span>Suporte & Ajuda</span>
                </Link>
              </li>
              <li>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center p-3 rounded text-sidebar-foreground hover:bg-sidebar-accent/50 transition-all duration-200"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  <span>Sair</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}